import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface FunctionalGroup {
  name: string;
  formula: string;
  description: string;
  color: string;
}

interface ReactionType {
  name: string;
  english: string;
  description: string;
  icon: string;
  color: string;
}

interface CommonMolecule {
  name: string;
  formula: string;
  use: string;
  icon: string;
}

interface OrganicChemTemplateProps {
  topic: string;
  subtitle: string;
  functionalGroups: FunctionalGroup[];
  reactions: ReactionType[];
  molecules: CommonMolecule[];
  bondTypes: { name: string; description: string; symbol: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: OrganicChemTemplateProps = {
  topic: '有机化学与分子世界',
  subtitle: 'Organic Chemistry & Molecular World',
  functionalGroups: [
    {
      name: '羟基',
      formula: '-OH',
      description: '醇类和酚类的标志性官能团，赋予分子亲水性',
      color: '#34d399',
    },
    {
      name: '羧基',
      formula: '-COOH',
      description: '有机酸的核心，氨基酸的关键组成部分',
      color: '#6ee7b7',
    },
    {
      name: '氨基',
      formula: '-NH₂',
      description: '蛋白质的基石，具有碱性和亲核性',
      color: '#a7f3d0',
    },
    {
      name: '醛基',
      formula: '-CHO',
      description: '醛类化合物的特征，具有强还原性',
      color: '#d1fae5',
    },
  ],
  reactions: [
    {
      name: '取代反应',
      english: 'Substitution',
      description: '一个原子或基团替换另一个',
      icon: '🔄',
      color: '#34d399',
    },
    {
      name: '加成反应',
      english: 'Addition',
      description: '不饱和键断裂，新原子加入',
      icon: '➕',
      color: '#059669',
    },
    {
      name: '消除反应',
      english: 'Elimination',
      description: '脱去小分子，形成不饱和键',
      icon: '➖',
      color: '#6ee7b7',
    },
    {
      name: '聚合反应',
      english: 'Polymerization',
      description: '单体连接成高分子长链',
      icon: '🔗',
      color: '#a7f3d0',
    },
  ],
  molecules: [
    { name: '乙醇', formula: 'C₂H₅OH', use: '消毒剂、燃料、溶剂', icon: '🍷' },
    { name: '葡萄糖', formula: 'C₆H₁₂O₆', use: '生命能量的直接来源', icon: '🍬' },
    { name: '阿司匹林', formula: 'C₉H₈O₄', use: '百年经典解热镇痛药', icon: '💊' },
    { name: '咖啡因', formula: 'C₈H₁₀N₄O₂', use: '中枢神经兴奋剂', icon: '☕' },
  ],
  bondTypes: [
    { name: '单键', description: '共享一对电子', symbol: 'C—C' },
    { name: '双键', description: '共享两对电子', symbol: 'C═C' },
    { name: '三键', description: '共享三对电子', symbol: 'C≡C' },
    { name: '苯环', description: '离域π键共轭体系', symbol: '⌬' },
  ],
  stats: [
    { value: '1000万+', label: '已知有机化合物数量' },
    { value: 'C', label: '碳是有机化学的核心元素' },
    { value: '4', label: '碳原子可形成的共价键数' },
  ],
  bottomQuote:
    '有机化学是生命的化学——从DNA双螺旋到药物分子，碳的无穷组合创造了生命的奇迹',
};

export const OrganicChemTemplate: React.FC<
  Partial<OrganicChemTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const groupStart = 30;
  const reactionStart = 170;
  const benzeneStart = 200;
  const moleculeStart = 300;
  const bondStart = 380;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const benzeneDraw = interpolate(
    frame,
    [benzeneStart, benzeneStart + 80],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const hexPoints = Array.from({ length: 6 }).map((_, i) => {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    return {
      x: 200 + 90 * Math.cos(angle),
      y: 140 + 90 * Math.sin(angle),
    };
  });

  const hexPath = hexPoints.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x},${pt.y}`).join(' ') + ' Z';

  const innerHexPoints = Array.from({ length: 6 }).map((_, i) => {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    return {
      x: 200 + 50 * Math.cos(angle),
      y: 140 + 50 * Math.sin(angle),
    };
  });

  const innerHexPath = innerHexPoints.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x},${pt.y}`).join(' ') + ' Z';

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #052e16 0%, #011a0b 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating hexagonal "molecule" particles */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 293) % 1920;
        const y = (i * 167) % 1080;
        const size = 6 + (i % 6) * 2;
        const drift = Math.sin(frame * 0.02 + i * 0.9) * 7;
        const rotate = frame * 0.3 + i * 30;
        const pulse = 0.04 + 0.03 * Math.sin(frame * 0.04 + i * 0.6);
        const colors = ['#34d399', '#6ee7b7', '#059669', '#a7f3d0'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y + drift,
              width: size,
              height: size,
              borderRadius: 2,
              border: `1px solid ${colors[i % colors.length]}`,
              background: 'transparent',
              opacity: pulse,
              transform: `rotate(${rotate}deg)`,
              boxShadow: `0 0 ${size * 2}px ${colors[i % colors.length]}20`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#065f46',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#a7f3d0',
            flexShrink: 0,
          }}
        >
          🧪 有机化学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#d1fae5',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Functional Groups ===== */}
      <div style={{ position: 'absolute', top: 120, left: 36, width: 550 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          官能团
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.functionalGroups.map((fg, i) => {
            const s = spring({
              frame: frame - (groupStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#0a2618',
                  borderRadius: 14,
                  padding: '14px 18px',
                  border: `1.5px solid ${fg.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: fg.color }}>
                    {fg.name}
                  </span>
                  <span
                    style={{
                      fontSize: 20,
                      color: '#6ee7b7',
                      fontFamily: 'monospace',
                      fontWeight: 700,
                    }}
                  >
                    {fg.formula}
                  </span>
                </div>
                <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4 }}>
                  {fg.description}
                </div>
              </div>
            );
          })}
        </div>

        {/* Reaction Types */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            反应类型
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {p.reactions.map((r, i) => {
              const s = spring({
                frame: frame - (reactionStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: 'calc(50% - 4px)',
                    background: '#0a2618',
                    borderRadius: 12,
                    padding: '10px 14px',
                    border: `1px solid ${r.color}30`,
                    opacity: s,
                    transform: `translateX(${(1 - s) * 24}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      background: `${r.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 22,
                      flexShrink: 0,
                    }}
                  >
                    {r.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: r.color }}>
                      {r.name}
                    </div>
                    <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                      {r.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Benzene Ring SVG ===== */}
      <div style={{ position: 'absolute', top: 105, left: 720, width: 640 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 14,
            letterSpacing: '0.08em',
          }}
        >
          苯环结构 · 芳香性
        </div>
        <svg width="520" height="380" viewBox="0 0 400 293">
          <defs>
            <linearGradient id="benzGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#059669" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#34d399" stopOpacity="0.05" />
            </linearGradient>
          </defs>

          {/* Outer hexagon */}
          <path
            d={hexPath}
            fill="url(#benzGrad)"
            stroke="#34d399"
            strokeWidth="3"
            strokeDasharray="600"
            strokeDashoffset={600 * (1 - benzeneDraw)}
            strokeLinejoin="round"
          />

          {/* Inner circle (delocalized electrons) */}
          <circle
            cx="200" cy="140" r="50"
            fill="none"
            stroke="#6ee7b7"
            strokeWidth="2"
            strokeDasharray="314"
            strokeDashoffset={314 * (1 - benzeneDraw)}
            opacity="0.6"
          />

          {/* Inner hex dashed */}
          <path
            d={innerHexPath}
            fill="none"
            stroke="#059669"
            strokeWidth="1"
            strokeDasharray="4 3"
            opacity={0.3 * benzeneDraw}
          />

          {/* Carbon atoms at vertices */}
          {hexPoints.map((pt, i) => {
            const atomIn = spring({
              frame: frame - (benzeneStart + 30 + i * 10),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            return (
              <g key={i} opacity={atomIn}>
                <circle
                  cx={pt.x} cy={pt.y} r="16"
                  fill="#0a2618"
                  stroke="#34d399"
                  strokeWidth="2"
                />
                <text
                  x={pt.x} y={pt.y + 5}
                  fill="#34d399" fontSize="14" fontWeight="800"
                  textAnchor="middle"
                >
                  C
                </text>
              </g>
            );
          })}

          {/* H atoms */}
          {hexPoints.map((pt, i) => {
            const angle = (Math.PI / 3) * i - Math.PI / 2;
            const hx = 200 + 125 * Math.cos(angle);
            const hy = 140 + 125 * Math.sin(angle);
            const hIn = spring({
              frame: frame - (benzeneStart + 60 + i * 8),
              fps,
              config: { damping: 14, stiffness: 140 },
            });
            return (
              <g key={`h-${i}`} opacity={hIn}>
                <line
                  x1={pt.x} y1={pt.y} x2={hx} y2={hy}
                  stroke="#6ee7b740" strokeWidth="1.5"
                />
                <text
                  x={hx} y={hy + 5}
                  fill="#6ee7b7" fontSize="13" fontWeight="700"
                  textAnchor="middle"
                >
                  H
                </text>
              </g>
            );
          })}

          {/* Label */}
          <text x="200" y="290" fill="#94a3b8" fontSize="18" textAnchor="middle">
            C₆H₆ · 苯 (Benzene)
          </text>
        </svg>

        {/* Common Molecules */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 20,
              letterSpacing: '0.08em',
            }}
          >
            常见有机分子
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
            }}
          >
            {p.molecules.map((mol, i) => {
              const s = spring({
                frame: frame - (moleculeStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minWidth: 0,
                    background: '#0a2618',
                    borderRadius: 14,
                    padding: '10px 12px',
                    border: '1.5px solid #064e3b',
                    opacity: s,
                    transform: `translateX(${(1 - s) * 30}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: '#059669' + '20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 28,
                      flexShrink: 0,
                      boxShadow: '0 0 18px #05966930',
                    }}
                  >
                    {mol.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ fontSize: 22, fontWeight: 800, color: '#34d399' }}>
                        {mol.name}
                      </span>
                      <span
                        style={{
                          fontSize: 20,
                          color: '#a7f3d0',
                          fontFamily: 'monospace',
                        }}
                      >
                        {mol.formula}
                      </span>
                    </div>
                    <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                      {mol.use}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Bond Types ===== */}
      <div style={{ position: 'absolute', top: 120, right: 60, width: 400 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          化学键类型
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.bondTypes.map((bond, i) => {
            const s = spring({
              frame: frame - (bondStart + i * 14),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#0a2618',
                  borderRadius: 14,
                  padding: '14px 16px',
                  border: '1.5px solid #064e3b',
                  opacity: s,
                  transform: `translateY(${(1 - s) * 16}px)`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: '#059669' + '20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'monospace',
                      fontSize: 20,
                      fontWeight: 900,
                      color: '#34d399',
                      flexShrink: 0,
                    }}
                  >
                    {bond.symbol}
                  </div>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#a7f3d0' }}>
                      {bond.name}
                    </div>
                    <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                      {bond.description}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Molecular structure visualization */}
        <div style={{ marginTop: 30 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 20,
              letterSpacing: '0.08em',
            }}
          >
            分子结构示意
          </div>
          <svg width="400" height="250" viewBox="0 0 400 250">
            {/* Bonds */}
            {[
              [40, 110, 110, 110],
              [110, 45, 110, 110],
              [110, 175, 110, 110],
              [110, 110, 200, 110],
              [200, 35, 200, 110],
              [200, 185, 200, 110],
              [200, 110, 280, 110],
              [280, 110, 340, 110],
            ].map(([x1, y1, x2, y2], i) => (
              <line
                key={`bond-${i}`}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke="#064e3b" strokeWidth="2.5"
                opacity={interpolate(
                  frame,
                  [bondStart + 50, bondStart + 80],
                  [0, 0.6],
                  { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
                )}
              />
            ))}

            {/* Ethanol molecule CH3-CH2-OH */}
            {[
              { x: 40, y: 110, label: 'H', r: 22, color: '#6ee7b7' },
              { x: 110, y: 45, label: 'H', r: 22, color: '#6ee7b7' },
              { x: 110, y: 175, label: 'H', r: 22, color: '#6ee7b7' },
              { x: 110, y: 110, label: 'C', r: 28, color: '#34d399' },
              { x: 200, y: 110, label: 'C', r: 28, color: '#34d399' },
              { x: 200, y: 35, label: 'H', r: 22, color: '#6ee7b7' },
              { x: 200, y: 185, label: 'H', r: 22, color: '#6ee7b7' },
              { x: 280, y: 110, label: 'O', r: 28, color: '#f87171' },
              { x: 340, y: 110, label: 'H', r: 22, color: '#6ee7b7' },
            ].map((atom, i) => {
              const aIn = spring({
                frame: frame - (bondStart + 60 + i * 6),
                fps,
                config: { damping: 14, stiffness: 140 },
              });
              return (
                <g key={i} opacity={aIn}>
                  <circle
                    cx={atom.x} cy={atom.y} r={atom.r}
                    fill="#0a2618"
                    stroke={atom.color} strokeWidth="2.5"
                  />
                  <text
                    x={atom.x} y={atom.y + 7}
                    fill={atom.color} fontSize="22" fontWeight="800"
                    textAnchor="middle"
                  >
                    {atom.label}
                  </text>
                </g>
              );
            })}

            <text x="180" y="240" fill="#64748b" fontSize="18" textAnchor="middle">
              CH₃CH₂OH · 乙醇 (Ethanol)
            </text>
          </svg>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 90,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0a2618',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #064e3b',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <span
                  style={{ fontSize: 28, fontWeight: 900, color: '#34d399' }}
                >
                  {st.value}
                </span>
                <span style={{ fontSize: 20, color: '#94a3b8' }}>
                  {st.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: 36,
          right: 36,
          background: '#0a2618',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #065f46',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🧬</span>
          <span style={{ fontSize: 20, color: '#d1fae5', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #059669, #34d399, #a7f3d0)',
        }}
      />
    </AbsoluteFill>
  );
};
